// LinkTapsChartJS.js - Bar Chart Version for Main Page
import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import { InfoIcon, KeyboardArrowRightIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    TimeScale,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    TimeScale,
    ChartTooltip,
    Legend
);

const LinkTapsChartJS = ({ data = [], onViewMore }) => {
    const chartRef = useRef();

    // Helper function to safely format dates
    const formatDate = (dateInput) => {
        if (!dateInput) return '';

        try {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateInput);
            return 'Invalid Date';
        }
    };

    // Create default data for empty state with consistent date range
    const getDefaultData = () => {
        const startDate = new Date('2025-01-01T00:00:00.000Z');
        const endDate = new Date('2025-06-06T23:59:59.999Z');
        const defaultData = [];

        // Ensure we include the full day for both start and end dates
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
            defaultData.push({
                date: date.toISOString(), // Store full ISO date for proper time handling
                taps: 0
            });
        }

        return defaultData;
    };

    // Process data for chart display with consistent date formatting and range
    const chartData = useMemo(() => {
        console.log('Raw data received:', data);

        // Handle empty or invalid data
        if (!data) {
            console.log('No data provided');
            return [];
        }

        // If data is already in the correct format (array of tap events)
        if (Array.isArray(data)) {
            console.log('Data is array of tap events');
            return data;
        }

        // If data is an object with overTime property
        if (data.overTime && Array.isArray(data.overTime)) {
            console.log('Data has overTime array');
            return data.overTime;
        }

        // If data is an object with linkTaps property
        if (data.linkTaps && Array.isArray(data.linkTaps)) {
            console.log('Data has linkTaps array');
            return data.linkTaps;
        }

        console.log('Data format not recognized');
        return [];
    }, [data]);

    // Process the data for the chart
    const processedData = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            console.log('No chart data to process');
            return [];
        }

        // Create a map to store taps by date
        const dateMap = new Map();

        // Initialize the date range
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30); // Last 30 days
        const endDate = new Date();

        // Initialize all dates in range with 0 taps
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
            const dateStr = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            dateMap.set(dateStr, {
                date: dateStr,
                taps: 0
            });
        }

        // Process each tap event
        chartData.forEach(item => {
            const timestamp = item.timestamp || item.date || item.createdAt;
            if (!timestamp) return;

            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return;

            const dateStr = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            if (dateMap.has(dateStr)) {
                dateMap.get(dateStr).taps += 1;
            }
        });

        // Convert map to array and sort by date
        const result = Array.from(dateMap.values())
            .sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateA - dateB;
            });

        console.log('Processed chart data:', result);
        return result;
    }, [chartData]);

    // Calculate total taps
    const totalTaps = useMemo(() => {
        // First priority: use backend's total if available
        if (data && typeof data === 'object' && typeof data.total === 'number') {
            return data.total;
        }
        // Second priority: if data has overTime array, sum from that
        if (data && data.overTime && Array.isArray(data.overTime)) {
            return data.overTime.reduce((sum, item) => sum + (item.taps || 0), 0);
        }
        // Fallback: calculate from chart data
        return processedData.reduce((sum, item) => sum + item.taps, 0);
    }, [data, processedData]);

    // Prepare Chart.js data
    const chartJSData = {
        labels: processedData.map(item => item.date),
        datasets: [
            {
                label: 'Link Taps',
                data: processedData.map(item => item.taps),
                backgroundColor: '#4CAF50',
                borderColor: '#4CAF50',
                borderWidth: 1,
                barThickness: 2,
                maxBarThickness: 2,
                categoryPercentage: 0.3,
                barPercentage: 0.3,
            }
        ]
    };

    console.log('Chart.js data:', chartJSData);

    // Chart options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (items) => {
                        return items[0].label;
                    },
                    label: (item) => `Taps: ${item.parsed.y}`
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    font: {
                        size: 11
                    },
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    font: {
                        size: 11
                    }
                },
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '414px',
                position: 'relative',
                borderRadius: '18px',
                backgroundColor: '#FFFFFF',
                padding: '24px'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    padding: '32px 18px 26px 18px',
                    flexDirection: 'column'
                }}
            >
                {/* Header section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#333333',
                        fontSize: '12px',
                        minWidth: '240px'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                            Link Taps Over Time
                        </Typography>
                        <Tooltip title="Link taps over time">
                            <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                        </Tooltip>
                    </Box>

                    <Button
                        onClick={onViewMore}
                        variant="text"
                        endIcon={<KeyboardArrowRightIcon />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#828282',
                            padding: 0,
                            minWidth: 'auto',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#666666'
                            }
                        }}
                    >
                        View more
                    </Button>
                </Box>

                {/* Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        padding: '20px 0',
                        gap: '15px',
                        flexWrap: 'wrap'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Box
                            sx={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#29AEF8',
                                borderRadius: '4px'
                            }}
                        >
                            <svg width="16" height="14" viewBox="0 0 8 6" fill="none">
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#4f4f4f' }}>
                            Total Link Taps ({totalTaps})
                        </Typography>
                    </Box>
                </Box>

                {/* Chart */}
                <Box
                    sx={{
                        height: '300px',
                        width: '100%',
                        marginTop: '20px',
                        position: 'relative'
                    }}
                >
                    {processedData.length > 0 ? (
                        <Bar
                            ref={chartRef}
                            data={chartJSData}
                            options={options}
                        />
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', color: '#828282' }}>
                                No link tap data available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default LinkTapsChartJS;
